import type { AppId } from "./apps";

export type AppGrantStatus = "active" | "revoked" | "expired";

/**
 * A license to one app for one user, granted from the admin panel. Lives at
 * /appGrants/{uid}_{appId}: the deterministic id makes a second license for the
 * same user and app impossible by construction — re-granting overwrites.
 *
 * Payment happens outside the platform; this is only the record of access.
 */
export interface AppGrant {
  uid: string;
  appId: AppId;
  status: AppGrantStatus;
  /** Epoch ms. Always set: licenses can't be granted without an end date. */
  expiresAt: number;
  /** First time this user got this app. Kept across extensions and re-grants. */
  grantedAt: number;
  updatedAt: number;
  /** Uid of the admin who last changed it. */
  grantedBy: string;
  note: string | null;
}

export type LicenseRequestStatus = "pending" | "granted" | "dismissed";

/**
 * A user asking for access to a licensed app. Lives at
 * /licenseRequests/{uid}_{appId}, so asking again updates the same request
 * instead of piling duplicates in the admin's inbox.
 */
export interface LicenseRequest {
  uid: string;
  appId: AppId;
  email: string;
  displayName: string | null;
  company: string;
  /** Contact phone. Missing on requests made before it was asked. */
  phone?: string | null;
  message: string | null;
  status: LicenseRequestStatus;
  createdAt: number;
  updatedAt: number;
  /** When the notification email went out. Null if it never did. */
  notifiedAt: number | null;
}

export function appGrantId(uid: string, appId: AppId): string {
  return `${uid}_${appId}`;
}

export function licenseRequestId(uid: string, appId: AppId): string {
  return `${uid}_${appId}`;
}

/** Whether a license opens its app right now. */
export function isGrantCurrent(grant: Pick<AppGrant, "status" | "expiresAt">, now: number): boolean {
  return grant.status === "active" && grant.expiresAt > now;
}
